import {makeBorderClass} from 'components/Form/Input';
import React, {FC, useEffect, useRef, useState} from 'react';

import {TagComponent} from './TagComponent';
import {Tag} from 'api/types/tagTypes';

type Props = {
  tags: Tag[];
  allTags: Tag[];
  disabled?: boolean;
  allowInput?: boolean;
  errorMessage?: string;
  onNewTag: (tagId: string) => void;
  onDeleteTag: (tagId: string, tagIndex: number) => void;
};

export const TagsInputField: FC<Props> = ({
  tags,
  allTags,
  disabled,
  allowInput = true,
  errorMessage,
  onDeleteTag,
  onNewTag,
}) => {
  const [newTag, setNewTag] = useState('');

  // New tag buble measurer properties
  const inputMockRef = useRef<HTMLSpanElement>(null);
  const [inputWidth, setInputWidth] = useState(0);

  // Properties for easier focusing
  const inputRef = useRef<HTMLInputElement>(null);
  const focusNewTagInput = () => inputRef.current?.focus();

  // Recalculate the size of the input field every time newTag property changes.
  useEffect(() => {
    setInputWidth(inputMockRef.current?.clientWidth || 0);
  }, [newTag]);

  // Create a list of existing tag components
  const tagComponents = tags.map((tag, i) => (
    <TagComponent
      label={tag.name}
      key={tag.id}
      onDelete={() => onDeleteTag(tag.id, i)}
    />
  ));

  // Filter out tags that are already added and only show
  // tags that contain the name of the newly typed tag.
  const filteredTags = allTags.filter(t => {
    // Check if already in the list.
    const tagNames = tags.map(tg => tg.name);
    const isAddedAlready = tagNames.includes(t.name);
    if (isAddedAlready) return false;

    // Search entered phrase in the tag name.
    return t.name.toLowerCase().indexOf(newTag.toLowerCase()) !== -1;
  });

  // Style of the bubble for the new tag field. Also a reason why I never use Tailwind.css
  const newTagBubbleStyle =
    'bg-skyblue text-dodger-blue rounded-huge p-1 pl-2 pr-2 mr-2 mt-2';

  // Handle enter key press. When enter is pressed we want to select the first element.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Check if there is a first element, otherwise escape.
      const ft = filteredTags;
      if (ft.length < 1) return;

      // Get new tag and call the callback
      const firstTag = ft[0];
      onNewTag(firstTag.id);

      // Reset existing tag
      setNewTag('');
    }
  };

  const handleAutoTagClick = (tagId: string) => {
    onNewTag(tagId);
    setNewTag('');
  };

  // Remove all unsupported characters from the input before setting it to the new tag.
  const handleTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTag(e.target.value);
  };

  return (
    <>
      <div
        className={`bg-white p-2 pt-0 ${makeBorderClass(
          errorMessage
        )} relative`}
        style={{minHeight: 48}}
        onClick={focusNewTagInput}
      >
        {/* Existing tags */}
        {tagComponents}

        {/* New tag input field */}
        {allowInput && !disabled && (
          <div
            className={`inline-flex w-auto relative ${
              newTag.length > 0 ? newTagBubbleStyle : ''
            }`}
          >
            {/*
          This span will invisibly render text the same way the input would
          do, but unlike input this span will have the right width to fit
          all characters.
        */}
            <span ref={inputMockRef} className="select-none opacity-0 absolute">
              {/* Replacing with full stop because in Source Sans Pro it's very similar to white space */}
              {newTag.replace(/\s/g, '.')}
            </span>
            <input
              type="text"
              className={`bg-clear focus:outline-none ${!newTag ? 'mt-2' : ''}`}
              style={{
                width: `${inputWidth}px`,
                minWidth: newTag.length > 0 ? '0px' : '80px',
                background: 'none',
              }}
              ref={inputRef}
              value={newTag}
              onChange={handleTagChange}
              onKeyUp={handleKeyDown}
            />
          </div>
        )}

        {/* Autosuggest popup */}
        {!!newTag && filteredTags.length > 0 && (
          <div
            className="absolute mt-2 z-50 bg-white rounded-huge border border-skyblue rounded-large p-2 shadow-skyblue-200"
            style={{left: 0, right: 0, top: '100%'}}
            onClick={e => e.stopPropagation()}
          >
            <div className="relative">
              {filteredTags.map(t => (
                <div
                  key={t.id}
                  className="p-2 cursor-pointer rounded-xl hover:bg-skyblue"
                  onClick={() => handleAutoTagClick(t.id)}
                >
                  {t.name}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {errorMessage}
    </>
  );
};
